import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";
import { Box, Grid, GridSize, Typography } from "@mui/material";
import { ColsCol, ColsLayout } from "Components/ColsLayout/ColsLayout";
// import { Glass } from "Components/Glass";
import StyledDrawer, {
  StyledDrawerContainer,
  StyledDrawerTitle,
} from "Components/UI/StyledDrawer";
import TrafficTable from "features/analytics/components/TrafficTable";
import SalesTable from "features/analytics/components/SalesTable";
import { AttributionMetric } from "features/analytics/types/types";
import CampaignAnalysisAnalyzeFilters from "../components/CampaignAnalysisAnalyzeFilters";
import CampaignAnalysisTable from "../components/CampaignAnalysisTable";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import produce from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useKeyPress, useUpdateEffect } from "react-use";
import useAppendSearchState from "Hooks/useAppendSearchState";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import CampaignAnalysisProvider from "features/attributionAnalytics/providers/CampaignAnalysisProvider";
import { useAttributionContext } from "Providers/AttributionProvider";
import LeadsTable from "features/analytics/components/LeadsTable";
// import { TOP_BAR_HEIGHT } from "features/leftMenu/constants";
import { useMetaUtmEntites } from "features/dataConnectors/hooks/useMetaUtmEntites";
import GlassCard from "Components/GlassCard/GlassCard";
import { isEqual } from "lodash";

interface ICampaignAnalysisExplorerProps {
  mode: "explore" | "analyze";
  useMatchType: boolean;
  paidChannelAttributes: ApiChartAttribute[]; // from hoc
  projectId: number;
}

function CampaignAnalysisExplorer({
  mode,
  useMatchType,
  paidChannelAttributes,
  projectId,
}: ICampaignAnalysisExplorerProps) {
  const { model: attributionModel } = useAttributionContext();
  const { entites } = useMetaUtmEntites();
  const [colWidths, setColsWidth] = useState<GridSize[]>([9, 1, 1, 1]);
  const [selectedPlatforms, setSelectedPlatforms] = useAppendSearchState<
    string[]
  >("ca-p", []);
  const [selectedCampaigns, setSelectedCampaigns] = useAppendSearchState<
    string[]
  >("ca-c", []);
  const [selectedGroups, setSelectedGroups] = useAppendSearchState<string[]>(
    "ca-g",
    [],
  );
  const [selectedKeywords, setSelectedKeywords] = useAppendSearchState<
    string[]
  >("ca-k", []);
  const [step, setStep] = useAppendSearchState("ca-s", 0);
  const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [detailsDrawerAttributes, setDetailsDrawerAttributes] = useState<
    ApiChartAttribute[]
  >([]);
  const [detailsDrawerMetric, setDetailsDrawerMetic] = useState<ApiMetric>(
    ApiMetric.METRIC_NOT_SET,
  );
  const [isShiftPressed] = useKeyPress("Shift");
  const [conversionMetric, setConversionMetric] = useState<AttributionMetric>(
    AttributionMetric.MACRO,
  );
  const [publisherConversion, setPublisherConversion] = useState<ApiMetric>(
    ApiMetric.METRIC_PUBLISHER_CONVERSIONS,
  );

  const termConfig = useMemo(() => {
    if (
      selectedPlatforms?.length === 1 &&
      ["facebook", "instagram"].includes(selectedPlatforms[0].toLowerCase())
    ) {
      return {
        dimension: ApiDimension.DIMENSION_EXTERNAL_AD_ID,
        text: "Ads",
      };
    }

    if (useMatchType) {
      return {
        dimension: ApiDimension.DIMENSION_TERM_WITH_MATCH_TYPE,
        text: "Terms & Match Type",
      };
    }
    return {
      dimension: ApiDimension.DIMENSION_TERM,
      text: "Terms",
    };
  }, [useMatchType, selectedPlatforms]);

  const contentConfig = useMemo(() => {
    if (
      selectedPlatforms?.length === 1 &&
      ["facebook", "instagram"].includes(selectedPlatforms[0].toLowerCase())
    ) {
      return {
        dimension: ApiDimension.DIMENSION_CONTENT,
        text: "Ad Sets",
      };
    }
    return {
      dimension: ApiDimension.DIMENSION_CONTENT,
      text: "Content",
    };
  }, [selectedPlatforms]);

  useUpdateEffect(() => {
    if (mode === "analyze") {
      setStep(0);
    }
    setSelectedPlatforms([]);
    setSelectedCampaigns([]);
    setSelectedGroups([]);
    setSelectedKeywords([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const setSelected = useCallback(
    (state: string[], val: string) =>
      produce(state, (draft) => {
        if (isShiftPressed) {
          const idx = draft.findIndex((d) => d === val);
          if (idx >= 0) {
            draft.splice(idx, 1);
          } else {
            draft.push(val);
          }
        } else {
          draft.length = 0;
          draft.push(val);
        }
      }),
    [isShiftPressed],
  );

  const selected = useCallback(
    (initailStep: number) => {
      switch (initailStep) {
        case 1:
          return selectedCampaigns;
        case 2:
          return selectedGroups;
        case 3:
          return selectedKeywords;
        default:
          return selectedPlatforms;
      }
    },
    [selectedPlatforms, selectedCampaigns, selectedGroups, selectedKeywords],
  );

  const onFirstColSelect = useCallback(
    (toStep: number) => (val) => {
      const sel = selected(toStep - 1);
      const idx = (sel || []).findIndex((s) => s === val);
      let step = toStep;

      if (
        (isShiftPressed && idx >= 0 && (sel || []).length <= 1) ||
        (!isShiftPressed && idx >= 0)
      ) {
        step = toStep - 1;
      }

      setStep(step);

      switch (step) {
        case 1:
          if (toStep === step) {
            setSelectedPlatforms((s) => setSelected(s || [], val));
          }
          break;
        case 2:
          if (toStep === step) {
            setSelectedCampaigns((s) => setSelected(s || [], val));
          }
          break;
        case 3:
          if (toStep === step) {
            setSelectedGroups((s) => setSelected(s || [], val));
          }
          break;
        case 4:
          if (toStep === step) {
            setSelectedKeywords((s) => setSelected(s || [], val));
          }
          break;

        default:
          break;
      }
    },
    [
      isShiftPressed,
      setSelected,
      selected,
      setSelectedCampaigns,
      setSelectedGroups,
      setSelectedKeywords,
      setSelectedPlatforms,
      setStep,
    ],
  );

  // Col widths only — do not clear URL-hydrated selections on mount/refresh
  useEffect(() => {
    const nextWidths: GridSize[] =
      step === 1 && (selectedPlatforms || []).length > 0
        ? [1, 9, 1, 1]
        : step === 2 && (selectedCampaigns || []).length > 0
          ? [1, 1, 9, 1]
          : step === 3 && (selectedGroups || []).length > 0
            ? [1, 1, 1, 9]
            : step === 4 && (selectedKeywords || []).length > 0
              ? [1, 1, 1, 9]
              : [9, 1, 1, 1];

    setColsWidth((prev) => (isEqual(prev, nextWidths) ? prev : nextWidths));
  }, [
    step,
    selectedPlatforms,
    selectedCampaigns,
    selectedGroups,
    selectedKeywords,
  ]);

  // Clear deeper selections when the user changes step (skip mount so refresh keeps query state)
  useUpdateEffect(() => {
    switch (step) {
      case 0:
        setSelectedPlatforms([]);
        setSelectedCampaigns([]);
        setSelectedGroups([]);
        setSelectedKeywords([]);
        break;
      case 1:
        setSelectedCampaigns([]);
        setSelectedGroups([]);
        setSelectedKeywords([]);
        break;
      case 2:
        setSelectedGroups([]);
        setSelectedKeywords([]);
        break;
      case 3:
        setSelectedKeywords([]);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Memoize onClick handlers to prevent infinite re-renders
  const onPlatformClick = useCallback(
    (val: string) => onFirstColSelect(1)(val),
    [onFirstColSelect],
  );

  const onCampaignClick = useCallback(
    (val: string) => onFirstColSelect(2)(val),
    [onFirstColSelect],
  );

  const onGroupClick = useCallback(
    (val: string) => onFirstColSelect(3)(val),
    [onFirstColSelect],
  );

  const onMetricClick = useCallback(
    (metric: ApiMetric, dimension: ApiDimension, dimensionValue: string) => {
      setDetailsDrawerMetic(metric);
      setDetailsDrawerAttributes((attr) =>
        produce(attr, (draft) => {
          draft.length = 0;
          draft.push(...paidChannelAttributes);

          if (dimension === ApiDimension.DIMENSION_SOURCE) {
            draft.push({
              key: ApiDimension.DIMENSION_SOURCE,
              operator: ApiOperator.EQUAL,
              value: dimensionValue,
            });
            return;
          } else {
            (selectedPlatforms || []).forEach((s) => {
              draft.push({
                key: ApiDimension.DIMENSION_SOURCE,
                operator: ApiOperator.EQUAL,
                value: s,
              });
            });
          }

          if (dimension === ApiDimension.DIMENSION_CAMPAIGN) {
            draft.push({
              key: ApiDimension.DIMENSION_CAMPAIGN,
              operator: ApiOperator.EQUAL,
              value: dimensionValue,
            });
            return;
          } else {
            (selectedCampaigns || []).forEach((s) => {
              draft.push({
                key: ApiDimension.DIMENSION_CAMPAIGN,
                operator: ApiOperator.EQUAL,
                value: s,
              });
            });
          }

          if (dimension === contentConfig.dimension) {
            draft.push({
              key: contentConfig.dimension,
              operator: ApiOperator.EQUAL,
              value: dimensionValue,
            });
            return;
          } else {
            (selectedGroups || []).forEach((s) => {
              draft.push({
                key: contentConfig.dimension,
                operator: ApiOperator.EQUAL,
                value: s,
              });
            });
          }
          if (dimension === termConfig.dimension) {
            draft.push({
              key: termConfig.dimension,
              operator: ApiOperator.EQUAL,
              value: dimensionValue,
            });
            return;
          } else {
            (selectedKeywords || []).forEach((s) => {
              draft.push({
                key: termConfig.dimension,
                operator: ApiOperator.EQUAL,
                value: s,
              });
            });
          }
        }),
      );
      setOpenDetailsDrawer(true);
    },
    [
      selectedPlatforms,
      selectedCampaigns,
      selectedGroups,
      selectedKeywords,
      paidChannelAttributes,
      termConfig,
      contentConfig,
    ],
  );

  useEffect(() => {
    if (!openDetailsDrawer) {
      setDetailsDrawerAttributes([]);
    }
  }, [openDetailsDrawer]);

  return (
    <CampaignAnalysisProvider>
      {mode === "analyze" && (
        <Grid container spacing={2} sx={{ minHeight: 350 }} mb={2}>
          <Grid item xs={8}>
            <AnalyticsCard
              chart={{
                name: "Publisher Trend",
                template: ApiChartTemplate.TREND,
                type: ApiChartType.GRAPH,
                includePublisherReadyOnly: true,
                dimension: [
                  ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                  ApiDimension.DIMENSION_SOURCE,
                ],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_PUBLISHER_CLICKS],
                  },
                ],
                attributes: paidChannelAttributes,
                displayOptions: {
                  noFilters: true,
                  availableMetrics: [
                    ApiMetric.METRIC_PUBLISHER_CLICKS,
                    ApiMetric.METRIC_PUBLISHER_IMPRESSIONS,
                    ApiMetric.METRIC_PUBLISHER_CTR,
                    ApiMetric.METRIC_PUBLISHER_CPC,
                    ApiMetric.METRIC_SPEND,
                  ],
                },
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <AnalyticsCard
              chart={{
                name: "CPC By Publisher",
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.PIE,
                includePublisherReadyOnly: true,
                dimension: [ApiDimension.DIMENSION_SOURCE],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_PUBLISHER_CPC],
                  },
                ],
                attributes: paidChannelAttributes,
                displayOptions: {
                  noFilters: true,
                  showTotals: true,
                },
              }}
            />
          </Grid>
        </Grid>
      )}
      <GlassCard sx={{ height: "100%" }} boxProps={{ p: 0 }}>
        {mode === "analyze" ? (
          <>
            <Box py={2}>
              <CampaignAnalysisAnalyzeFilters
                selectedPlatforms={selectedPlatforms || []}
                setSelectedPlatforms={setSelectedPlatforms}
                selectedCampaigns={selectedCampaigns || []}
                setSelectedCampaigns={setSelectedCampaigns}
                selectedGroups={selectedGroups || []}
                setSelectedGroups={setSelectedGroups}
                selectedKeywords={selectedKeywords || []}
                setSelectedKeywords={setSelectedKeywords}
              />
            </Box>
            <CampaignAnalysisTable
              mode={mode}
              firstCols={[
                {
                  key: ApiDimension.DIMENSION_SOURCE,
                  text: "Platforms",
                },
                {
                  key: ApiDimension.DIMENSION_CAMPAIGN,
                  text: "Campaigns",
                },
                {
                  key: contentConfig.dimension,
                  text: contentConfig.text,
                },
                {
                  key: termConfig.dimension,
                  text: termConfig.text,
                },
              ]}
              selecteValues={selectedPlatforms || []}
              headerComponent={
                <CampaignAnalysisAnalyzeFilters
                  selectedPlatforms={selectedPlatforms || []}
                  setSelectedPlatforms={setSelectedPlatforms}
                  selectedCampaigns={selectedCampaigns || []}
                  setSelectedCampaigns={setSelectedCampaigns}
                  selectedGroups={selectedGroups || []}
                  setSelectedGroups={setSelectedGroups}
                  selectedKeywords={selectedKeywords || []}
                  setSelectedKeywords={setSelectedKeywords}
                />
              }
              conversionMetric={conversionMetric}
              setConversionMetric={setConversionMetric}
              attributionModel={attributionModel}
              publisherConversion={publisherConversion}
              setPublisherConversion={setPublisherConversion}
              onMetricClick={onMetricClick}
              filters={[
                ...(selectedPlatforms || []).map((sp) => ({
                  key: ApiDimension.DIMENSION_SOURCE,
                  operator: ApiOperator.CONTAINS,
                  value: sp,
                })),
                ...(selectedCampaigns || []).map((sc) => ({
                  key: ApiDimension.DIMENSION_CAMPAIGN,
                  operator: ApiOperator.CONTAINS,
                  value: sc,
                })),
                ...(selectedGroups || []).map((sg) => ({
                  key: contentConfig.dimension,
                  operator: ApiOperator.CONTAINS,
                  value: sg,
                })),
                ...(selectedKeywords || []).map((sk) => ({
                  key: termConfig.dimension,
                  operator: ApiOperator.CONTAINS,
                  value: sk,
                })),
              ]}
              currentStep={step || 0}
              step={0}
              adEntities={entites}
            />
          </>
        ) : (
          <ColsLayout>
            <ColsCol width={colWidths[0]}>
              <CampaignAnalysisTable
                mode={mode}
                title="Publishers"
                firstCols={[
                  {
                    key: ApiDimension.DIMENSION_SOURCE,
                    text: "Platforms",
                  },
                ]}
                onClick={onPlatformClick}
                selecteValues={selectedPlatforms || []}
                conversionMetric={conversionMetric}
                setConversionMetric={setConversionMetric}
                attributionModel={attributionModel}
                publisherConversion={publisherConversion}
                setPublisherConversion={setPublisherConversion}
                filters={[]}
                currentStep={step || 0}
                step={0}
                onTitleClick={() => setStep(0)}
                onMetricClick={onMetricClick}
                showProviderIcon
                adEntities={entites}
              />
            </ColsCol>
            <ColsCol width={colWidths[1]}>
              <CampaignAnalysisTable
                mode={mode}
                title="Campaigns"
                firstCols={[
                  {
                    key: ApiDimension.DIMENSION_CAMPAIGN,
                    text: "Campaigns",
                  },
                ]}
                onClick={onCampaignClick}
                selecteValues={selectedCampaigns || []}
                conversionMetric={conversionMetric}
                setConversionMetric={setConversionMetric}
                attributionModel={attributionModel}
                publisherConversion={publisherConversion}
                setPublisherConversion={setPublisherConversion}
                filters={[
                  ...(selectedPlatforms || []).map((sp) => ({
                    key: ApiDimension.DIMENSION_SOURCE,
                    value: sp,
                  })),
                ]}
                currentStep={step || 0}
                step={1}
                onTitleClick={() => setStep(1)}
                onMetricClick={onMetricClick}
                adEntities={entites}
              />
            </ColsCol>
            <ColsCol width={colWidths[2]}>
              <CampaignAnalysisTable
                mode={mode}
                title={contentConfig.text}
                firstCols={[
                  {
                    key: contentConfig.dimension,
                    text: contentConfig.text,
                  },
                ]}
                onClick={onGroupClick}
                selecteValues={selectedGroups || []}
                conversionMetric={conversionMetric}
                setConversionMetric={setConversionMetric}
                attributionModel={attributionModel}
                publisherConversion={publisherConversion}
                setPublisherConversion={setPublisherConversion}
                filters={[
                  ...(selectedPlatforms || []).map((sp) => ({
                    key: ApiDimension.DIMENSION_SOURCE,
                    value: sp,
                  })),
                  ...(selectedCampaigns || []).map((sc) => ({
                    key: ApiDimension.DIMENSION_CAMPAIGN,
                    value: sc,
                  })),
                ]}
                currentStep={step || 0}
                step={2}
                onTitleClick={() => setStep(2)}
                onMetricClick={onMetricClick}
                adEntities={entites}
              />
            </ColsCol>
            <ColsCol width={colWidths[3]}>
              <CampaignAnalysisTable
                mode={mode}
                title={termConfig.text}
                firstCols={[
                  {
                    key: termConfig.dimension,
                    text: termConfig.text,
                  },
                ]}
                selecteValues={selectedKeywords || []}
                conversionMetric={conversionMetric}
                setConversionMetric={setConversionMetric}
                attributionModel={attributionModel}
                publisherConversion={publisherConversion}
                setPublisherConversion={setPublisherConversion}
                filters={[
                  ...(selectedPlatforms || []).map((sp) => ({
                    key: ApiDimension.DIMENSION_SOURCE,
                    value: sp,
                  })),
                  ...(selectedCampaigns || []).map((sc) => ({
                    key: ApiDimension.DIMENSION_CAMPAIGN,
                    value: sc,
                  })),
                  ...(selectedGroups || []).map((sg) => ({
                    key: contentConfig.dimension,
                    value: sg,
                  })),
                ]}
                currentStep={step || 0}
                step={3}
                onTitleClick={() => setStep(3)}
                onMetricClick={onMetricClick}
                adEntities={entites}
              />
            </ColsCol>
          </ColsLayout>
        )}
      </GlassCard>
      <StyledDrawer
        anchor="right"
        open={openDetailsDrawer}
        onClose={() => setOpenDetailsDrawer(false)}
        hideDivider
      >
        <StyledDrawerTitle>
          {detailsDrawerMetric === ApiMetric.METRIC_SESSIONS_COUNT ? (
            <>
              <Typography variant="subtitle1">Explore Traffic</Typography>
              <Typography variant="body2">
                Explore Traffic from Paid Platforms for your selected Provider /
                Campaign etc
              </Typography>
            </>
          ) : detailsDrawerMetric === ApiMetric.METRIC_LEADS_COUNT ? (
            <>
              <Typography variant="subtitle1">Explore Leads</Typography>
              <Typography variant="body2">
                Explore Leads from Paid Platforms for your selected Provider /
                Campaign etc
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="subtitle1">Explore Sales</Typography>
              <Typography variant="body2">
                Explore Sales from Paid Platforms for your selected Provider /
                Campaign etc
              </Typography>
            </>
          )}
        </StyledDrawerTitle>
        <StyledDrawerContainer sx={{ minWidth: "85vw" }}>
          {detailsDrawerMetric === ApiMetric.METRIC_SESSIONS_COUNT ? (
            <TrafficTable attributes={detailsDrawerAttributes} />
          ) : detailsDrawerMetric === ApiMetric.METRIC_LEADS_COUNT ? (
            <LeadsTable
              attributes={detailsDrawerAttributes}
              projectId={projectId}
              havingAttributionMetric={conversionMetric}
              havingAttributionModel={
                attributionModel || ApiAttributionModel.LAST
              }
            />
          ) : (
            <SalesTable attributes={detailsDrawerAttributes} />
          )}
        </StyledDrawerContainer>
      </StyledDrawer>
    </CampaignAnalysisProvider>
  );
}

export default withPaidChannelAttributes(CampaignAnalysisExplorer);
